import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { BsInfoCircle, BsShare, BsPlus, BsTrash } from "react-icons/bs";
import { CardComp, ButtonComp } from "../../../components";
import {
  useGetMemberReferralLinkQuery,
  useGetMemberReferralFriendsQuery,
  useSendMemberReferralInviteMutation,
} from "../../../store/member/member-referral";
import RoleFormSkeletonLoader from "../../../components/Common/RoleFormSkeletonLoader";
import moment from "moment";

const MAX_EMAILS = 5;
const MAX_PHONES = 5;

function summarizeInviteResult(result) {
  const emailSent = result?.emails?.sent?.length || 0;
  const emailFailed = result?.emails?.failed || [];
  const phoneSent = result?.phones?.sent?.length || 0;
  const phoneFailed = result?.phones?.failed || [];

  const parts = [];
  if (emailSent > 0) parts.push(`${emailSent} email${emailSent === 1 ? "" : "s"} sent`);
  if (emailFailed.length > 0) {
    const reason = emailFailed[0]?.reason;
    parts.push(
      `${emailFailed.length} email${emailFailed.length === 1 ? "" : "s"} failed${
        reason ? `: ${reason}` : ""
      }`
    );
  }
  if (phoneSent > 0) parts.push(`${phoneSent} SMS sent`);
  if (phoneFailed.length > 0) {
    const reason = phoneFailed[0]?.reason;
    parts.push(
      `${phoneFailed.length} SMS failed${reason ? `: ${reason}` : ""}`
    );
  }
  return parts.length ? parts.join("; ") : "Invite processed";
}

function formatFriendDate(iso) {
  if (!iso) return "";
  const m = moment(iso);
  return m.isValid() ? m.format("D MMM YYYY") : "";
}

function statusMeta(status) {
  if (status === "joined") {
    return {
      label: "Joined",
      bg: "#E8F6EE",
      color: "#1B7A3D",
      detail: "Joined",
    };
  }
  if (status === "failed") {
    return {
      label: "Failed",
      bg: "rgba(139, 49, 58, 0.12)",
      color: "#8b313a",
      detail: "Invite failed",
    };
  }
  return {
    label: "Pending",
    bg: "#FCEFE2",
    color: "#C45C12",
    detail: "Awaiting sign-up",
  };
}

function FriendsReferredSection() {
  const { data, isLoading, isError, refetch, isFetching } =
    useGetMemberReferralFriendsQuery();

  const people = Array.isArray(data?.people) ? data.people : [];
  const total = Number(data?.total) || people.length;
  const countLabel = total === 1 ? "1 person" : `${total} people`;

  return (
    <div className="mt-4">
      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h6 className="mb-0 fw-bold text-black1">Friends you referred</h6>
        {!isLoading ? (
          <span className="small text-muted">{countLabel}</span>
        ) : null}
      </div>

      <div
        className="rounded"
        style={{
          background: "#fff",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
        aria-busy={isLoading || isFetching}
      >
        {isLoading ? (
          <div className="p-3">
            <RoleFormSkeletonLoader />
          </div>
        ) : isError ? (
          <button
            type="button"
            className="btn btn-link text-start w-100 p-3"
            onClick={() => refetch()}
          >
            Could not load referrals. Tap to retry.
          </button>
        ) : people.length === 0 ? (
          <p className="mb-0 small text-muted p-3">
            No referrals yet. Share your link or send an invite to see friends
            here.
          </p>
        ) : (
          <ul className="list-unstyled mb-0">
            {people.map((friend, index) => {
              const meta = statusMeta(friend?.status);
              const dateLabel = formatFriendDate(friend?.date);
              const detail =
                friend?.status === "failed" && friend?.failure_reason
                  ? friend.failure_reason
                  : dateLabel
                    ? `${meta.detail} · ${dateLabel}`
                    : meta.detail;
              const initial = (friend?.initial || friend?.name || "?").charAt(0);

              return (
                <li
                  key={friend?.id || index}
                  className="d-flex align-items-center gap-3 px-3 py-3"
                  style={{
                    borderBottom:
                      index < people.length - 1 ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: 40,
                      height: 40,
                      background: "#f7f7f7",
                      color: "#8b313a",
                      fontWeight: 600,
                    }}
                    aria-hidden
                  >
                    {String(initial).toUpperCase()}
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold text-black1 text-truncate">
                      {friend?.name || "Friend"}
                    </div>
                    <div className="small text-muted text-truncate">{detail}</div>
                  </div>
                  <span
                    className="rounded-pill px-2 py-1 small flex-shrink-0"
                    style={{
                      background: meta.bg,
                      color: meta.color,
                      fontWeight: 500,
                    }}
                  >
                    {meta.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Member referral card: copy/share link + multi email/phone invite (max 5 each).
 * Hidden entirely when branch flag enabled=false.
 */
export default function MemberReferralCard() {
  const { data: linkData, isLoading, isError, error, refetch } =
    useGetMemberReferralLinkQuery();
  const [sendInvite, { isLoading: isSending }] =
    useSendMemberReferralInviteMutation();

  const [emails, setEmails] = useState([""]);
  const [phones, setPhones] = useState([""]);

  const referralUrl = linkData?.url || "";
  const shareMessage = linkData?.message || "";
  const enabled = linkData?.enabled !== false;

  const copyLink = async () => {
    if (!referralUrl) {
      toast.error("Referral link is not ready yet.");
      return;
    }
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied");
    } catch {
      toast.error("Could not copy link. Please copy it manually.");
    }
  };

  const shareNative = async () => {
    if (!referralUrl) {
      toast.error("Referral link is not ready yet.");
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join my cooperative",
          text: shareMessage || referralUrl,
          url: referralUrl,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    await copyLink();
  };

  const handleOptionalInvite = async (e) => {
    e.preventDefault();
    const cleanEmails = emails.map((v) => v.trim()).filter(Boolean);
    const cleanPhones = phones.map((v) => v.trim()).filter(Boolean);
    if (!cleanEmails.length && !cleanPhones.length) {
      toast.error("Enter at least one friend email or phone number");
      return;
    }
    if (cleanEmails.length > MAX_EMAILS || cleanPhones.length > MAX_PHONES) {
      toast.error(`You can invite at most ${MAX_EMAILS} emails and ${MAX_PHONES} phones`);
      return;
    }
    try {
      const result = await sendInvite({
        emails: cleanEmails,
        phones: cleanPhones,
      }).unwrap();
      toast.success(summarizeInviteResult(result));
      setEmails([""]);
      setPhones([""]);
    } catch (err) {
      toast.error(err?.data?.message || "Could not send invite");
    }
  };

  const errorMessage = useMemo(() => {
    if (!isError) return null;
    return error?.data?.message || "Could not load your referral link.";
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="mt-4" aria-busy="true" aria-label="Loading referral card">
        <RoleFormSkeletonLoader />
      </div>
    );
  }

  if (!enabled) {
    return null;
  }

  return (
    <CardComp cardClassName="border-0 mt-4 shadow-sm">
      <div className="d-flex align-items-start gap-2 mb-3">
        <BsShare size={22} style={{ color: "#31778c", marginTop: 2 }} />
        <div>
          <h5 className="mb-1 fw-bold text-black1">Invite friends to join</h5>
          <p className="mb-0 small text-muted">
            Share your personal link so new members can join your cooperative
            from anywhere. Distance is no longer a barrier to growth.
          </p>
        </div>
      </div>

      {errorMessage ? (
        <div className="alert alert-warning py-2 small mb-3">
          {errorMessage}{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <label className="fw-semibold small text-black1 mb-1">
            Your referral link
          </label>
          <div className="input-group mb-2">
            <input
              type="text"
              className="form-control"
              readOnly
              value={referralUrl}
              aria-label="Referral link"
              style={{ background: "#fafafa" }}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={copyLink}
            >
              Copy
            </button>
          </div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <ButtonComp
              btnText="Share link"
              btnClassName="border-0 text-white rounded py-2 px-3"
              ButtonInlineStyle={{
                backgroundColor: "#31778c",
                minWidth: "fit-content",
              }}
              onClick={shareNative}
            />
          </div>

          <form
            onSubmit={handleOptionalInvite}
            className="rounded p-3"
            style={{
              background: "rgba(49, 119, 140, 0.06)",
              border: "1px solid rgba(49, 119, 140, 0.2)",
            }}
          >
              <div className="d-flex align-items-start gap-2 mb-3">
                <BsInfoCircle
                  size={16}
                  style={{ color: "#31778c", marginTop: 3 }}
                />
                <div>
                  <p className="mb-1 fw-semibold small text-black1">
                    Invite by email or SMS
                  </p>
                  <p className="mb-0 small text-muted">
                    Email invites are free. SMS invites are charged to your
                    cooperative branch SMS wallet. Up to {MAX_EMAILS} emails and{" "}
                    {MAX_PHONES} phones per submit.
                  </p>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold small">Friend emails</span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    disabled={emails.length >= MAX_EMAILS}
                    onClick={() => setEmails((prev) => [...prev, ""])}
                  >
                    <BsPlus /> Add email
                  </button>
                </div>
                {emails.map((value, index) => (
                  <div key={`email-${index}`} className="d-flex gap-2 mb-2">
                    <div className="flex-grow-1">
                      <input
                        type="email"
                        className="form-control"
                        placeholder="friend@example.com"
                        name={`friend_email_${index}`}
                        value={value}
                        onChange={(e) => {
                          const next = [...emails];
                          next[index] = e.target.value;
                          setEmails(next);
                        }}
                        style={{ background: "#fafafa" }}
                      />
                    </div>
                    {emails.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        aria-label="Remove email"
                        onClick={() =>
                          setEmails((prev) => prev.filter((_, i) => i !== index))
                        }
                      >
                        <BsTrash />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold small">Friend phones (SMS)</span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    disabled={phones.length >= MAX_PHONES}
                    onClick={() => setPhones((prev) => [...prev, ""])}
                  >
                    <BsPlus /> Add phone
                  </button>
                </div>
                {phones.map((value, index) => (
                  <div key={`phone-${index}`} className="d-flex gap-2 mb-2">
                    <div className="flex-grow-1">
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="e.g. +2348012345678"
                        name={`friend_phone_${index}`}
                        value={value}
                        onChange={(e) => {
                          const next = [...phones];
                          next[index] = e.target.value;
                          setPhones(next);
                        }}
                        style={{ background: "#fafafa" }}
                      />
                    </div>
                    {phones.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        aria-label="Remove phone"
                        onClick={() =>
                          setPhones((prev) => prev.filter((_, i) => i !== index))
                        }
                      >
                        <BsTrash />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <ButtonComp
                  type="submit"
                  btnText={isSending ? "Sending…" : "Send invites"}
                  disabled={isSending}
                  loading={isSending}
                  btnClassName="border-0 text-white rounded py-2 px-3"
                  ButtonInlineStyle={{
                    backgroundColor: "#8b313a",
                    minWidth: "fit-content",
                  }}
                />
              </div>
            </form>

          <FriendsReferredSection />
        </>
      )}
    </CardComp>
  );
}
